import React, { useState, useRef, useEffect } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, PaperPlaneRight, Microphone, ArrowRight } from "@phosphor-icons/react";
import EventCard from "../../components/EventCard";
import { Spinner } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";

const SUGGESTIONS = [
  "Find a comedy show this weekend below ₹1,500",
  "Something fun for my kids tomorrow",
  "Music events tonight with my friends",
  "Best value events near me this week",
];

export default function AskAI() {
  const { city } = useOutletContext();
  const [messages, setMessages] = useState([
    { role: "ai", text: `Hi! I'm your EventVexa assistant. Tell me what you're in the mood for in ${city} and I'll find real events for you.` },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const endRef = useRef(null);
  const recogRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/customer/find", { query: q, city });
      setMessages((m) => [...m, { role: "ai", text: data.reply, events: data.events }]);
    } catch (e) { toast.error(apiErr(e)); setMessages((m) => [...m, { role: "ai", text: "Sorry, I couldn't process that. Please try again." }]); }
    finally { setLoading(false); }
  };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input isn't supported in this browser."); return; }
    if (listening) { recogRef.current?.stop(); return; }
    const r = new SR(); recogRef.current = r; r.lang = "en-IN"; r.interimResults = false;
    r.onresult = (e) => { const t = e.results[0][0].transcript; setInput(t); setTimeout(() => send(t), 100); };
    r.onend = () => setListening(false);
    r.onerror = () => { setListening(false); toast.error("Voice input failed."); };
    setListening(true); r.start();
  };

  return (
    <div className="mx-auto max-w-3xl px-4 md:px-6 py-8 flex flex-col min-h-[calc(100vh-4rem)]">
      <div className="flex items-center gap-2 mb-6">
        <span className="grid place-items-center w-10 h-10 rounded-full bg-primary/10 text-primary"><Sparkle weight="fill" size={20} /></span>
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Ask AI to Find an Event</h1>
          <p className="text-sm text-muted-foreground">Type or speak — I only recommend real, available events.</p>
        </div>
      </div>

      <div className="flex-1 space-y-5">
        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={m.role === "user" ? "flex justify-end" : ""}>
            {m.role === "user" ? (
              <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-foreground text-background px-4 py-2.5">{m.text}</div>
            ) : (
              <div>
                <div className="max-w-[85%] rounded-2xl rounded-bl-sm glass border border-border px-4 py-3">{m.text}</div>
                {m.events?.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="ai-results">
                    {m.events.map((e) => <EventCard key={e.id} event={{ ...e, is_featured: false }} />)}
                  </div>
                )}
                {m.events && m.events.length === 0 && <p className="text-sm text-muted-foreground mt-2">No matching events right now.</p>}
              </div>
            )}
          </motion.div>
        ))}
        {loading && <div className="flex items-center gap-2 text-muted-foreground text-sm"><Spinner className="w-4 h-4 text-primary" /> Finding the best events…</div>}
        <div ref={endRef} />
      </div>

      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2 my-5">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => send(s)} data-testid="ai-suggestion" className="px-3 py-2 rounded-full border border-border bg-card text-sm hover:border-primary/40 btn-press inline-flex items-center gap-1.5">
              <Sparkle size={14} className="text-primary" /> {s}
            </button>
          ))}
        </div>
      )}

      <div className="sticky bottom-4 mt-4">
        <div className="flex items-center gap-2 glass border border-border rounded-full p-1.5 pl-4">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="e.g. comedy show this weekend under ₹1500" className="border-0 bg-transparent focus-visible:ring-0 px-0" data-testid="ai-input" />
          <button onClick={toggleVoice} data-testid="ai-voice" className={`w-10 h-10 grid place-items-center rounded-full btn-press ${listening ? "bg-primary text-white animate-pulse" : "bg-muted"}`}><Microphone size={18} /></button>
          <Button onClick={() => send()} disabled={loading} className="w-10 h-10 p-0 rounded-full" data-testid="ai-send"><PaperPlaneRight size={18} /></Button>
        </div>
      </div>
    </div>
  );
}
